import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions';
import get from 'lodash.get';
import { FindingRouter } from '../../router/FindingRouter';
import { searchBrowse as routes } from '../../router/routes/searchBrowse';
import { stripRoutingSegment, addCdnQueryParams } from '../../utils/uriUtils';
import {
    toggleRequestInFlight,
    toggleHashFragmentResolved,
    updateUriRef,
    updateZipCode,
    setSemanticSearchTerm,
} from '../../actions/filterActions';
import { getFollowTypeFromUri } from '../../utils/favoritesUtils';
import {
    hasPersonalizedFilterQueryParam,
    getPersonalizedFilterValues,
    getUrlWithoutPersonalizedFilterParam,
    getPersonalizedFilterAction,
    shouldApplyPersonalizedFilter,
    getUserZipCode,
    getPersonalizedFilterUriRef,
} from '../../shared/helpers/personalizedFiltersHelpers';
import { LoadingComponent } from '../../components/global/LoadingComponent';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
import { MAX_AD_LIMIT } from '../../constants/sbConstants';
import {
    getOriginalQueryHashParamValue,
    removeOriginalQueryHashParamValue,
} from '../../utils/hashFragmentHelpers';
import { getNumberOfTiles, getPageSizeOfTiles } from '../../utils/adHelpers';
const { SEARCH } = pageTypes;

export class SbSharedRefetchContainerComponent extends React.Component {
    constructor() {
        super();
        this.personalizedFilterValues = [];
        this.refetchCallback = this.refetchCallback.bind(this);
    }

    componentDidMount() {
        const { pathname, search, hash } = window.location;
        const { itemSearch = {} } = this.props;
        const { vanityUriRef, searchCorrections = {} } = itemSearch;
        const oqParam = getOriginalQueryHashParamValue(hash);
        const semanticSearchTerm = searchCorrections.semanticSearchTerm || oqParam;

        if (this.isHashFragmentSearchPage()) {
            if (hash) {
                this.props.updateUriRef({ uriRef: `${pathname}${search}${hash}` });
            } else {
                this.props.toggleHashFragmentResolved(true);
            }
        } else {
            // set new URI ref in reducer
            this.props.updateUriRef({ uriRef: itemSearch.displayUriRef });
        }

        this.initRouter();

        this.props.setSemanticSearchTerm(semanticSearchTerm);

        if (vanityUriRef) {
            // for force facet  or semantic or spell corrected search pages, graphql returned an updated uri,
            // update it in the browser
            this.router.navigate(vanityUriRef, {
                replace: true,
                semanticSearchTerm,
            });
        }

        this.personalizedFilterValues = getPersonalizedFilterValues();

        this.props.updateZipCode(getUserZipCode());
    }

    componentDidUpdate(prevProps) {
        const {
            regionalInfo,
            generatedUriRef,
            page: newPage,
            onSbRefetch,
            pageSize: newPageSize,
            shippingFilters,
        } = this.props;
        const { isSbAdsEnabled } = this.context;
        /**
         * Make sure we have query params in both uri refs before comparing them to see if they've
         * changed.
         */
        const oldUriRef = addCdnQueryParams(prevProps.generatedUriRef);
        const newUriRef = addCdnQueryParams(generatedUriRef);
        const uriRefChanged =
            (oldUriRef !== '' || this.isHashFragmentSearchPage()) && newUriRef !== oldUriRef;
        const pageSizeChanged = newPageSize !== prevProps.pageSize;

        if (uriRefChanged || pageSizeChanged) {
            const variables = {
                page:
                    newPage === prevProps.page &&
                    // if uriRef changed because filter added/removed, reset to page 1
                    (uriRefChanged ||
                        // make sure page size actually changed and not just from page with ads to page with no ads
                        Math.abs(newPageSize - prevProps.pageSize) > MAX_AD_LIMIT)
                        ? 1
                        : newPage,
                uriRef: newUriRef,
                followSearchPages: [generatedUriRef],
                followSearchTypes: [getFollowTypeFromUri(generatedUriRef)],
                // reset number of tiles base on the new uriRef
                first: pageSizeChanged
                    ? newPageSize
                    : getNumberOfTiles({
                          path: generatedUriRef,
                          // we need to map old numOfTiles back to default page size (60/120) to get correct numOfTiles for this new uriRef
                          pageSize: getPageSizeOfTiles({
                              uriRef: prevProps.generatedUriRef,
                              numOfTiles: prevProps.pageSize,
                          }),
                          isSbAdsEnabled,
                      }),
                disableForceFacet: true,
            };

            // 1. set state to loading
            this.props.toggleRequestInFlight(true);

            // 2. do refetch
            onSbRefetch(variables, null, this.refetchCallback);

            // 3. update to new relay vars
            this.props.updateRelayVariables(variables);
        }

        // Handle ?personalizedfilter= query params
        const regionsByZipCode = get(regionalInfo, '[0].regionsByZipCode');
        if (this.personalizedFilterValues.length && regionsByZipCode) {
            // bug in our current version of eslint, false positive for no-unused-vars in for loop
            // eslint-disable-next-line
            for (const personalizedFilterValue of this.personalizedFilterValues) {
                if (
                    shouldApplyPersonalizedFilter({ personalizedFilterValue, shippingFilters }) &&
                    getPersonalizedFilterAction(personalizedFilterValue)
                ) {
                    this.props.updateUriRef({
                        uriRef: getPersonalizedFilterUriRef({
                            filterName: personalizedFilterValue,
                            filterValues: regionsByZipCode,
                            uriRef: generatedUriRef,
                        }),
                    });
                }
            }
        } else if (hasPersonalizedFilterQueryParam()) {
            const urlWithoutPersonalizedFilter = getUrlWithoutPersonalizedFilterParam();
            this.router.navigate(urlWithoutPersonalizedFilter);
        }
    }

    isHashFragmentSearchPage() {
        const isSearch = this.props.itemSearch.pageType === SEARCH;
        return (
            isSearch &&
            !this.context.isSearchQueryApplied &&
            get(this.props, 'viewer.enableSearchLandingPage')
        );
    }

    refetchCallback() {
        const { semanticSearchTerm } = this.props;
        const displayUriRef = get(this.props, 'itemSearch.displayUriRef');

        this.props.toggleRequestInFlight(false);

        this.props.updateUriRef({ uriRef: displayUriRef });

        if (this.isHashFragmentSearchPage() && !this.props.isHashFragmentResolved) {
            this.props.toggleHashFragmentResolved(true);
        }

        if (displayUriRef) {
            this.router.navigate(displayUriRef, {
                semanticSearchTerm,
            });
        }
    }

    initRouter() {
        this.router = new FindingRouter({ routes });
        this.router.history.listen(({ pathname, search, hash }, action) => {
            if (action === 'POP') {
                const newHash = removeOriginalQueryHashParamValue(hash);
                const uriRef = `${pathname}${search}${newHash}`;
                this.props.updateUriRef({ uriRef: stripRoutingSegment(uriRef) });
            }
        });
    }

    render() {
        const { refetchInFlight, isHashFragmentResolved } = this.props;

        if ((this.isHashFragmentSearchPage() && !isHashFragmentResolved) || refetchInFlight) {
            return <LoadingComponent />;
        } else {
            return null;
        }
    }
}

SbSharedRefetchContainerComponent.contextType = ServerVarsContext;

SbSharedRefetchContainerComponent.defaultProps = {
    setSemanticSearchTerm: () => {},
};

SbSharedRefetchContainerComponent.propTypes = {
    updateUriRef: PropTypes.func.isRequired,
    onSbRefetch: PropTypes.func.isRequired,
    itemSearch: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    // TODO INFRA: Should be required
    generatedUriRef: PropTypes.string,
    toggleRequestInFlight: PropTypes.func.isRequired,
    updateRelayVariables: PropTypes.func.isRequired,
    regionalInfo: PropTypes.array,
    updateZipCode: PropTypes.func.isRequired,
    shippingFilters: PropTypes.array,
    refetchInFlight: PropTypes.bool.isRequired,
    toggleHashFragmentResolved: PropTypes.func.isRequired,
    isHashFragmentResolved: PropTypes.bool.isRequired,
    setSemanticSearchTerm: PropTypes.func.isRequired,
    semanticSearchTerm: PropTypes.string,
};

function mapStateToProps(state) {
    const { filters, relayVariables } = state;
    const { generatedUriRef, page } = filters;
    const {
        variables: { first },
    } = relayVariables;

    return {
        page,
        generatedUriRef,
        pageSize: first,
        refetchInFlight: filters.refetchInFlight,
        isHashFragmentResolved: filters.isHashFragmentResolved,
        semanticSearchTerm: filters.semanticSearchTerm,
    };
}

export const SbSharedRefetchContainer = createFragmentContainer(
    connect(
        mapStateToProps,
        {
            updateUriRef,
            updateRelayVariables,
            toggleRequestInFlight,
            toggleHashFragmentResolved,
            updateZipCode,
            setSemanticSearchTerm,
        }
    )(SbSharedRefetchContainerComponent),
    {
        viewer: graphql`
            fragment SbSharedRefetchContainer_viewer on Viewer {
                enableSearchLandingPage: isEnabled(key: "enableSearchLandingPage")
            }
        `,
        itemSearch: graphql`
            fragment SbSharedRefetchContainer_itemSearch on ItemSearchQueryConnection {
                pageType
                vanityUriRef
                displayUriRef
                searchCorrections {
                    semanticSearchTerm
                }
            }
        `,
        regionalInfo: graphql`
            fragment SbSharedRefetchContainer_regionalInfo on RegionalInfo @relay(plural: true) {
                regionsByZipCode {
                    displayName
                    urlLabel
                }
            }
        `,
        shippingFilters: graphql`
            fragment SbSharedRefetchContainer_shippingFilters on ShippingFilterType
                @relay(plural: true) {
                ...personalizedFiltersHelpers_shippingFilters @relay(mask: false)
            }
        `,
    }
);
